/**
 * @fileoverview Combined energy/gradient evaluator.
 *
 * Composes the individual force kernels into the single
 * `evaluate(coords, ligand, receptorContext, options)` entry point the L-BFGS
 * driver (`minimizer/minimize.js`) consumes. The total potential is the sum of
 *
 *     U = U_bonds + U_angles + U_torsions          (intramolecular bonded)
 *       + U_ligand_nonbonded                        (intramolecular LJ + Coulomb)
 *       + U_receptor                                (ligand <-> static receptor)
 *       + U_restraints                              (optional harmonic position restraints)
 *
 * There is no GBSA / implicit-solvent term: mm-server is vacuum-only, so the
 * engine matches it exactly with vacuum Coulomb + LJ.
 *
 * Hot-path discipline: the gradient buffer is owned by the evaluator and reused
 * across calls. `evaluate` zeroes it once, then each kernel accumulates into it
 * (the kernels never zero). No allocation occurs on the hot path -- the
 * per-call scratch (the gradient buffer, the restraint lookup) is preallocated
 * at construction time.
 *
 * Units throughout: nm, kJ/mol, e, rad (the engine contract).
 *
 * See: proto/src/chemistry/LocalMinimizer/types.js,
 *      forces/{bonded,nonbonded,receptor}.js.
 */

import { evalBonds, evalAngles, evalTorsions } from './bonded.js';
import { evalLigandNonbonded } from './nonbonded.js';

/**
 * @typedef {import('../types.js').LigandInput} LigandInput
 * @typedef {import('../types.js').MinimizationOptions} MinimizationOptions
 * @typedef {import('../types.js').PositionalRestraint} PositionalRestraint
 * @typedef {import('./receptor.js').ReceptorContext} ReceptorContext
 */

/**
 * @typedef {Object} EvaluatorEnergyResult
 * @property {number}       energy    Total potential energy, kJ/mol.
 * @property {Float64Array} gradient  Flat length 3*N gradient, kJ/mol/nm. Owned
 *   by the evaluator and reused across calls -- copy if you need to retain it.
 */

/**
 * Nonbonded cutoff (nm) used for the ligand-internal LJ + Coulomb sum.
 *
 * mm-server runs `NonbondedForce::NoCutoff`: every pair interacts regardless
 * of distance. We mirror that with an effectively
 * infinite cutoff for the ligand-internal sum so the shifted-cutoff term in
 * `evalLigandNonbonded` vanishes (the shift at r=inf is 0) and the ligand
 * energy matches mm-server exactly. The receptor candidate set is already
 * pre-pruned by `prepareReceptor`, so its `evaluate` is likewise driven with a
 * large cutoff (all retained candidates interact).
 *
 * @type {number}
 */
const NO_CUTOFF = 1e9;

/**
 * Stateful combined evaluator. Construct once per ligand (its buffer sizes are
 * fixed by `ligand.topology.nAtoms`), then call {@link Evaluator#evaluate}
 * repeatedly during a minimization.
 *
 * The class form keeps the preallocated gradient buffer and the decoded
 * restraint arrays on the instance so the hot path never allocates. A thin
 * functional wrapper {@link evaluate} is exported for one-shot use and for the
 * step-4.12 finite-difference tests.
 */
export class Evaluator {
    /**
     * @param {LigandInput} ligand            Topology + parameters + (initial) coords.
     * @param {ReceptorContext} [receptorContext]  Prepared receptor, or null for vacuum-only.
     * @param {MinimizationOptions} [options] Restraint / cutoff knobs. Only the
     *   restraint-related fields are read here; the driver owns iteration knobs.
     */
    constructor(ligand, receptorContext = null, options = {}) {
        const { topology, parameters } = ligand;
        const { nAtoms } = topology;

        /** @type {LigandInput} */
        this.ligand = ligand;
        /** @type {ReceptorContext|null} */
        this.receptorContext = receptorContext || null;
        /** @type {number} */
        this.nAtoms = nAtoms;
        /** @type {number} */
        this.cutoff = NO_CUTOFF;

        // Preallocated gradient buffer reused across every evaluate() call.
        /** @type {Float64Array} */
        this.gradient = new Float64Array(3 * nAtoms);

        // Cache the topology/parameter buffers as plain fields for a tight
        // hot path (avoids repeated property chains in the inner kernels).
        this.bonds = topology.bonds;
        this.angles = topology.angles;
        this.torsions = topology.torsions;
        this.bondK = parameters.bondK;
        this.bondR0 = parameters.bondR0;
        this.angleK = parameters.angleK;
        this.angleTheta0 = parameters.angleTheta0;
        this.torsionTermsPerTorsion = parameters.torsionTermsPerTorsion;
        this.torsionK = parameters.torsionK;
        this.torsionN = parameters.torsionN;
        this.torsionPhase = parameters.torsionPhase;
        this.charges = parameters.charges;
        this.sigma = parameters.sigma;
        this.epsilon = parameters.epsilon;
        this.exclusions = parameters.exclusions;
        this.scaledPairs = parameters.scaledPairs;

        // Decode positional restraints into flat parallel arrays once, so the
        // hot path is a single tight loop with no object property access.
        const restraints = options.positionalRestraints || [];
        const nR = restraints.length;
        /** @type {Int32Array} Atom index of each restraint. */
        this.restraintAtom = new Int32Array(nR);
        /** @type {Float64Array} Stiffness k (kJ/mol/nm^2) of each restraint. */
        this.restraintK = new Float64Array(nR);
        /** @type {Float64Array} Target positions, flat 3*nR (nm). */
        this.restraintR0 = new Float64Array(3 * nR);
        for (let i = 0; i < nR; i++) {
            const r = restraints[i];
            this.restraintAtom[i] = r.atom;
            this.restraintK[i] = r.k;
            this.restraintR0[3 * i] = r.r0[0];
            this.restraintR0[3 * i + 1] = r.r0[1];
            this.restraintR0[3 * i + 2] = r.r0[2];
        }
        /** @type {number} */
        this.nRestraints = nR;
    }

    /**
     * Evaluate the total potential energy and accumulate its gradient.
     *
     * The gradient buffer is zeroed here, then each term accumulates into it.
     * The returned `gradient` aliases the instance buffer (reused next call).
     *
     * @param {Float64Array} coords   Flat length 3*N ligand coordinates (nm).
     * @returns {EvaluatorEnergyResult}
     */
    evaluate(coords) {
        const grad = this.gradient;
        grad.fill(0);

        let energy = 0;

        // --- Bonded terms (each accumulates into grad, returns energy). ---
        energy += evalBonds(coords, this.bonds, this.bondK, this.bondR0, grad);
        energy += evalAngles(coords, this.angles, this.angleK, this.angleTheta0, grad);
        energy += evalTorsions(
            coords,
            this.torsions,
            this.torsionTermsPerTorsion,
            this.torsionK,
            this.torsionN,
            this.torsionPhase,
            grad,
        );

        // --- Ligand-internal nonbonded (LJ + Coulomb, exclusion-aware). ---
        energy += evalLigandNonbonded(
            coords,
            this.charges,
            this.sigma,
            this.epsilon,
            this.exclusions,
            this.scaledPairs,
            this.cutoff,
            grad,
        );

        // --- Receptor (ligand <-> static receptor), if prepared. ---
        if (this.receptorContext && this.receptorContext.nCandidates > 0) {
            energy += this.receptorContext.evaluate(
                coords,
                this.charges,
                this.sigma,
                this.epsilon,
                this.cutoff,
                grad,
            );
        }

        // --- Positional restraints (harmonic to reference, no 1/2 prefactor
        //     so dE/dx = 2 k (x - r0), matching the bond/angle convention). ---
        if (this.nRestraints > 0) {
            energy += this.evalRestraints(coords, grad);
        }

        return { energy, gradient: grad };
    }

    /**
     * Harmonic positional-restraint term:
     *     E = Sum_r k_r * |x_atom - r0|^2          (no 1/2 prefactor)
     *     dE/dx = 2 k_r (x_atom - r0)
     *
     * @param {Float64Array} coords   Flat length 3*N (nm).
     * @param {Float64Array} grad     Flat length 3*N gradient accumulator.
     * @returns {number}              Restraint energy contribution (kJ/mol).
     */
    evalRestraints(coords, grad) {
        let energy = 0;
        const n = this.nRestraints;
        for (let r = 0; r < n; r++) {
            const atom = this.restraintAtom[r];
            const k = this.restraintK[r];
            const a3 = 3 * atom;
            const r3 = 3 * r;

            const dx = coords[a3] - this.restraintR0[r3];
            const dy = coords[a3 + 1] - this.restraintR0[r3 + 1];
            const dz = coords[a3 + 2] - this.restraintR0[r3 + 2];

            energy += k * (dx * dx + dy * dy + dz * dz);

            const twoK = 2 * k;
            grad[a3] += twoK * dx;
            grad[a3 + 1] += twoK * dy;
            grad[a3 + 2] += twoK * dz;
        }
        return energy;
    }
}

/**
 * Functional one-shot wrapper around {@link Evaluator}. Builds a fresh
 * evaluator (allocating its buffers) and runs a single evaluation. Convenient
 * for tests and the finite-difference check; the driver should construct an
 * {@link Evaluator} once and reuse it to avoid per-call allocation.
 *
 * @param {Float64Array} coords            Flat length 3*N ligand coords (nm).
 * @param {LigandInput}  ligand
 * @param {ReceptorContext} [receptorContext]
 * @param {MinimizationOptions} [options]
 * @returns {EvaluatorEnergyResult}
 */
export function evaluate(coords, ligand, receptorContext = null, options = {}) {
    const ev = new Evaluator(ligand, receptorContext, options);
    return ev.evaluate(coords);
}

/**
 * Build the `evalFn(coords) -> {energy, gradient}` closure that the line search
 * and L-BFGS driver consume (matching the `EvalFn` contract in
 * `minimizer/lineSearch.js`). The closure reuses a single {@link Evaluator}, so
 * repeated calls allocate nothing.
 *
 * @param {LigandInput}  ligand
 * @param {ReceptorContext} [receptorContext]
 * @param {MinimizationOptions} [options]
 * @returns {{ evalFn: (coords: Float64Array) => EvaluatorEnergyResult, evaluator: Evaluator }}
 */
export function makeEvalFn(ligand, receptorContext = null, options = {}) {
    const evaluator = new Evaluator(ligand, receptorContext, options);
    const evalFn = (coords) => evaluator.evaluate(coords);
    return { evalFn, evaluator };
}
